import type { RouteInfo, RouteSection, RoutesConfig } from './types';

function isSectionWithIndex(value: unknown): value is RouteSection {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  return typeof (value as RouteSection).index === 'string';
}

/**
 * Format a camelCase key into a human-readable label
 * Example: "myRouteName" -> "My Route Name"
 */
function formatLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
    .trim();
}

/**
 * Recursively flatten the routes object into a list of route info objects
 */
export function flattenRoutes(
  obj: RoutesConfig,
  parentSection = '',
  parentPath = '',
  result: RouteInfo[] = []
): RouteInfo[] {
  Object.entries(obj).forEach(([key, value]) => {
    if (typeof value === 'string') {
      // This is a route path
      const label = formatLabel(key);

      result.push({
        path: value,
        label,
        section: parentSection || 'root',
        parent: parentPath || undefined,
      });
    } else if (typeof value === 'object' && value !== null) {
      // This is a section with nested routes
      const sectionLabel = formatLabel(key);

      // If the section has an 'index' route, add it
      if (isSectionWithIndex(value)) {
        result.push({
          path: value.index || '',
          label: sectionLabel,
          section: parentSection || 'root',
          parent: parentPath || undefined,
        });
      }

      // Recurse into nested routes
      const parentForChildren = isSectionWithIndex(value) ? value.index : parentPath;
      flattenRoutes(value as RoutesConfig, sectionLabel, parentForChildren, result);
    }
  });

  return result;
}

/**
 * Get the current section from the pathname
 */
export function getCurrentSection(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) {
    return 'home';
  }
  return parts[0];
}

/**
 * Get related pages based on current location
 */
export function getRelatedPages(pathname: string, allRoutes: RouteInfo[]): RouteInfo[] {
  const currentSection = getCurrentSection(pathname);

  // Find routes in the same section
  const relatedRoutes = allRoutes.filter(
    (route) => route.section.toLowerCase() === currentSection.toLowerCase() && route.path !== pathname
  );

  // Limit to top 10 most relevant
  return relatedRoutes.slice(0, 10);
}

/**
 * Get parent and sibling pages
 */
export function getNavigationContext(pathname: string, allRoutes: RouteInfo[]) {
  const currentRoute = allRoutes.find((r) => r.path === pathname);
  const currentSection = getCurrentSection(pathname);

  // Find parent route
  const parent = currentRoute?.parent ? allRoutes.find((r) => r.path === currentRoute.parent) : undefined;

  // Find sibling routes (routes with same parent)
  const siblings = currentRoute?.parent
    ? allRoutes.filter((r) => r.parent === currentRoute.parent && r.path !== pathname)
    : allRoutes.filter((r) => r.section === currentSection && r.path !== pathname && !r.parent);

  // Find child routes
  const children = allRoutes.filter((r) => r.parent === pathname);

  return {
    current: currentRoute,
    parent,
    siblings: siblings.slice(0, 10),
    children,
  };
}
